import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Checkbox, UIButton } from '../primitives';
import { useConsentStore } from '../../store/consentStore';
import { trackClientEvent } from '../../analytics/track';
import LegalDocumentViewer from './LegalDocumentViewer';
import SheetHeader from '../SheetHeader';

const REQUIRED_KEYS = ['terms', 'age18', 'biometric'];
const EMPTY_VALUES = {
  terms: false,
  age18: false,
  biometric: false,
  marketing: false,
};
const SWIPE_DISMISS_DISTANCE = 64;
const SHEET_CLOSE_DELAY_MS = 180;

function ConsentFields({
  values,
  toggle,
  openLegalDocument,
  biometricCopy,
  showMarketing,
  t,
}) {
  return (
    <div className="mt-5 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <Checkbox
          checked={values.terms}
          onChange={toggle('terms')}
          aria-labelledby="consent-terms-label"
          data-testid="consent-terms"
        />
        <p id="consent-terms-label" className="font-sans text-plan leading-relaxed">
          <Trans
            i18nKey="consent.checkbox.terms"
            components={{
              terms: (
                <button
                  type="button"
                  className="text-icon-accent underline"
                  onClick={openLegalDocument('terms')}
                />
              ),
              privacy: (
                <button
                  type="button"
                  className="text-icon-accent underline"
                  onClick={openLegalDocument('privacy')}
                />
              ),
              aup: (
                <button
                  type="button"
                  className="text-icon-accent underline"
                  onClick={openLegalDocument('aup')}
                />
              ),
              refund: (
                <button
                  type="button"
                  className="text-icon-accent underline"
                  onClick={openLegalDocument('refund')}
                />
              ),
            }}
          />
        </p>
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          checked={values.age18}
          onChange={toggle('age18')}
          aria-labelledby="consent-age-label"
          data-testid="consent-age18"
        />
        <p id="consent-age-label" className="font-sans text-plan leading-relaxed">
          {t('consent.checkbox.age18')}
        </p>
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          checked={values.biometric}
          onChange={toggle('biometric')}
          aria-labelledby="consent-biometric-label"
          data-testid="consent-biometric"
        />
        <p id="consent-biometric-label" className="font-sans text-plan leading-relaxed">
          {biometricCopy}
        </p>
      </div>

      {showMarketing && (
        <div className="border-t border-border-default pt-4">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={values.marketing}
              onChange={toggle('marketing')}
              aria-labelledby="consent-marketing-label"
              data-testid="consent-marketing"
            />
            <p id="consent-marketing-label" className="font-sans text-plan leading-relaxed">
              {t('consent.checkbox.marketing')}{' '}
              <span className="text-text-secondary">
                {t('consent.checkbox.marketingOptional')}
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ConsentGate({
  mode = 'blocking',
  open = true,
  onDismiss,
}) {
  const { t, i18n } = useTranslation();
  const currentVersion = useConsentStore((state) => state.currentVersion);
  const versionChanged = useConsentStore((state) => state.versionChanged);
  const acceptConsent = useConsentStore((state) => state.acceptConsent);
  const completeDeferredConsent = useConsentStore(
    (state) => state.completeDeferredConsent,
  );
  const isSubmitting = useConsentStore((state) => state.isSubmitting);
  const error = useConsentStore((state) => state.error);
  const [values, setValues] = useState(EMPTY_VALUES);
  const [openDocument, setOpenDocument] = useState(null);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [sheetClosing, setSheetClosing] = useState(false);
  const touchStartYRef = useRef(null);
  const dismissTimerRef = useRef(null);
  const isModal = mode === 'modal';

  const dismissModal = useCallback(() => {
    if (!isModal || sheetClosing) return;
    setSheetClosing(true);
    setSheetVisible(false);
    if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = window.setTimeout(() => {
      setValues(EMPTY_VALUES);
      setOpenDocument(null);
      onDismiss?.();
      dismissTimerRef.current = null;
    }, SHEET_CLOSE_DELAY_MS);
  }, [isModal, onDismiss, sheetClosing]);

  useEffect(() => {
    if (!open) {
      setSheetVisible(false);
      setSheetClosing(false);
      return undefined;
    }
    setValues(EMPTY_VALUES);
    setOpenDocument(null);
    if (!isModal) return undefined;

    setSheetClosing(false);
    const frame = window.requestAnimationFrame(() => setSheetVisible(true));
    return () => window.cancelAnimationFrame(frame);
  }, [isModal, open]);

  useEffect(() => () => {
    if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
  }, []);

  useEffect(() => {
    if (!isModal || !open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !openDocument) {
        dismissModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dismissModal, isModal, open, openDocument]);

  useEffect(() => {
    if (!isModal || !open || typeof document === 'undefined') return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
    };
  }, [isModal, open]);

  if (isModal && !open) return null;

  const remainingCount = REQUIRED_KEYS.filter((key) => !values[key]).length;
  const canContinue = remainingCount === 0;
  const biometricCopy = t('consent.checkbox.biometric');

  const toggle = (key) => (checked) => {
    setValues((current) => ({ ...current, [key]: checked }));
  };
  const openLegalDocument = (slug) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenDocument(slug);
  };
  const submit = async () => {
    if (!canContinue || isSubmitting) return;
    await acceptConsent({
      ...values,
      documentVersion: currentVersion,
      locale: i18n.resolvedLanguage || i18n.language || 'en',
    });

    if (isModal) {
      setValues(EMPTY_VALUES);
      completeDeferredConsent(values.marketing);
    } else {
      trackClientEvent('consent_accepted', {
        trigger: 'version_update',
        marketing: Boolean(values.marketing),
      });
    }
  };
  const handleTouchStart = (event) => {
    touchStartYRef.current = event.touches?.[0]?.clientY ?? null;
  };
  const handleTouchEnd = (event) => {
    const endY = event.changedTouches?.[0]?.clientY;
    const startY = touchStartYRef.current;
    touchStartYRef.current = null;
    if (
      isModal &&
      startY != null &&
      endY != null &&
      endY - startY >= SWIPE_DISMISS_DISTANCE
    ) {
      dismissModal();
    }
  };

  const fields = (
    <>
      <ConsentFields
        values={values}
        toggle={toggle}
        openLegalDocument={openLegalDocument}
        biometricCopy={biometricCopy}
        showMarketing={!isModal}
        t={t}
      />

      {!isModal && (
        <p className="mt-4 text-center font-sans text-chip-label leading-relaxed text-text-secondary">
          {t('consent.warning.violation')}
        </p>
      )}

      {error && (
        <p className="mt-4 text-center font-sans text-plan text-text-secondary" role="alert">
          {error?.response?.status === 409
            ? t('consent.error.versionMismatch')
            : t('consent.error.submit')}
        </p>
      )}
    </>
  );

  const continueLabel = isSubmitting
    ? t('consent.button.submitting')
    : canContinue
      ? t('consent.button.continue')
      : t('consent.button.remaining', { count: remainingCount });

  const continueButton = (
    <UIButton
      type="primary"
      size="lg"
      disabled={!canContinue || isSubmitting}
      onClick={submit}
      className="w-full disabled:cursor-not-allowed disabled:opacity-40"
      data-testid="consent-continue"
    >
      {continueLabel}
    </UIButton>
  );

  return (
    <>
      {isModal ? (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-4"
          data-testid="consent-modal"
        >
          <button
            type="button"
            className={[
              'absolute inset-0 bg-black/[0.72] transition-opacity duration-200 ease-out',
              sheetVisible && !sheetClosing ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
            aria-label={t('common.close')}
            data-testid="consent-modal-backdrop"
            onClick={dismissModal}
          />
          <section
            className={[
              'relative z-10 flex max-h-[calc(100dvh-24px)] w-full max-w-lg flex-col overflow-hidden',
              'rounded-t-home-24 bg-popup-gradient text-text-primary shadow-2xl',
              'sm:max-h-[calc(100dvh-32px)] sm:rounded-home-24',
              'transform-gpu transition-transform duration-200 ease-out',
              sheetVisible && !sheetClosing ? 'translate-y-0' : 'translate-y-full',
            ].join(' ')}
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-modal-title"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-6">
              <SheetHeader
                title={t('consent.modal.title')}
                subtitle={t('consent.modal.subtitle')}
                titleId="consent-modal-title"
                onClose={dismissModal}
                closeTestId="consent-modal-close"
                contentClassName="mb-0"
              />
              {fields}
            </div>
            <div
              className="shrink-0 px-6 pt-3"
              style={{ paddingBottom: 'calc(var(--safe-bottom, 0px) + 16px)' }}
            >
              {continueButton}
            </div>
          </section>
        </div>
      ) : (
        <div
          className="fixed inset-0 z-40 flex flex-col bg-bg-app text-text-primary"
          role="dialog"
          aria-modal="true"
          aria-labelledby="consent-title"
          data-testid="consent-gate"
        >
          <div className="mx-auto flex min-h-0 w-full max-w-lg flex-1 flex-col px-body-x pb-6 pt-4">
            <div className="min-h-0 flex-1 overflow-y-auto pt-consent-content-offset">
              <h1 id="consent-title" className="font-sans text-display-32">
                {t(versionChanged ? 'consent.modal.title' : 'consent.title')}
              </h1>
              <p className="mt-2 font-sans text-plan text-text-secondary">
                {t(versionChanged ? 'consent.modal.subtitle' : 'consent.subtitle')}
              </p>
              {fields}
            </div>
            <div className="mt-4 shrink-0">{continueButton}</div>
          </div>
        </div>
      )}

      {openDocument && (
        <LegalDocumentViewer
          slug={openDocument}
          locale={i18n.resolvedLanguage || i18n.language || 'en'}
          onClose={() => setOpenDocument(null)}
        />
      )}
    </>
  );
}
